import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    fetchCancellationPayments,
    CancellationPayment,
    createManualCancellationPayment,
    ManualPaymentResponse,
    voidCancellationPayment,
    fetchPaymentStats,
    PaymentStats,
    resendPaymentLink,
    VOID_REASONS,
    VoidReason,
    checkCancellationPayment,
    fetchSimpleChaserStats,
    runSimpleChasers,
    SimpleChaserStats,
    SimpleChaserResult,
    markManualPayment,
    checkWorldpayAvailable
} from '../api/payments';
import { fetchClaims, Claim } from '../api/claims';
import { fetchFeeDecisionOptions, RingfenceFee } from '../api/cancellationRingfence';
import Modal from './ModalTrue';
import { getCheckoutPayment } from '../api/worldpayCheckout';
import { WorldpayPhonePaymentPanel } from './WorldpayPhonePaymentPanel';
import { WorldpayUnmatchedQueue } from './WorldpayUnmatchedQueue';
import { WorldpayNeedsReconciliationQueue } from './WorldpayNeedsReconciliationQueue';
import { WorldpayConfirmationBadge } from './WorldpayConfirmationBadge';
import { amountStillOwed } from '../utils/paymentBalance';
import './AdminPayments.css';

// Shown when the backend doesn't say who can run chasers (it normally sends
// chaserRunnersLabel, built from its PAYMENT_CHASER_RUNNERS setting).
const NO_CHASER_PERMISSION = "You don't have permission to run chasers.";
const CHASER_STATUS_UNAVAILABLE = "Couldn't load chaser status — refresh to try again";

type StatusFilter = 'all' | 'pending' | 'completed' | 'cancelled' | 'failed' | 'expired' | 'voided' | 'partial';

// Payment status filter options - force rebuild
const statusOptions: Array<{ label: string; value: StatusFilter }> = [
  { label: 'All statuses', value: 'all' },
  { label: 'Pending', value: 'pending' },
  { label: 'Paid', value: 'completed' },
  { label: 'Cancelled', value: 'cancelled' },
  { label: 'Failed', value: 'failed' },
  { label: 'Expired', value: 'expired' },
  { label: 'Voided', value: 'voided' },
  { label: 'Partial', value: 'partial' }
];

const currencyFormatter = new Intl.NumberFormat('en-GB', {
  style: 'currency',
  currency: 'GBP'
});

// Long enough that typing "216" is one request, short enough that the figure
// lands before a staff member reaches for the submit button.
const MANUAL_PREVIEW_DEBOUNCE_MS = 400;

const formatDate = (value?: string) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return value;
  }
};

// Chaser escalation period in days
const CHASER_ESCALATION_DAYS = 14;

/**
 * Determine the chaser status of a payment for row styling
 * Returns: 'normal', 'chased' (orange), or 'overdue' (red)
 */
const getChaserStatus = (payment: CancellationPayment): 'normal' | 'chased' | 'overdue' => {
  const status = (payment.status || '').toLowerCase();

  // Paid, voided, or completed payments are normal
  if (['completed', 'paid', 'succeeded', 'voided'].includes(status)) {
    return 'normal';
  }

  // Check if chaser was sent
  if (!payment.chaserSentAt) {
    return 'normal';
  }

  // Calculate days since chaser
  try {
    const chaserDate = new Date(payment.chaserSentAt);
    const now = new Date();
    const daysSinceChaser = Math.floor((now.getTime() - chaserDate.getTime()) / (1000 * 60 * 60 * 24));

    if (daysSinceChaser >= CHASER_ESCALATION_DAYS) {
      return 'overdue'; // RED - 14+ days after chaser
    }
    return 'chased'; // ORANGE - chased but within 14 days
  } catch {
    return 'chased';
  }
};

/**
 * Get CSS class for payment row based on chaser and void status
 */
const getPaymentRowClass = (payment: CancellationPayment): string => {
  const status = (payment.status || '').toLowerCase();
  const isVoided = status === 'voided';

  if (isVoided) {
    return 'bg-gray-50 opacity-75';
  }

  const chaserStatus = getChaserStatus(payment);
  switch (chaserStatus) {
    case 'chased':
      return 'payment-row-chased hover:bg-orange-100';
    case 'overdue':
      return 'payment-row-overdue hover:bg-red-100';
    default:
      return 'hover:bg-gray-50';
  }
};

const AdminCancellationPayments: React.FC = () => {
  const [payments, setPayments] = useState<CancellationPayment[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [manualAmount, setManualAmount] = useState('');
  const [manualDescription, setManualDescription] = useState('');
  const [selectedClaims, setSelectedClaims] = useState<Claim[]>([]);
  const [claimSearchTerm, setClaimSearchTerm] = useState('');
  const [claimSearchResults, setClaimSearchResults] = useState<Claim[]>([]);
  const [searchingClaims, setSearchingClaims] = useState(false);
  const [claimSearchError, setClaimSearchError] = useState<string | null>(null);
  const [manualError, setManualError] = useState<string | null>(null);
  const [manualSuccess, setManualSuccess] = useState<ManualPaymentResponse | null>(null);
  const [creatingManualPayment, setCreatingManualPayment] = useState(false);
  const [manualModalOpen, setManualModalOpen] = useState(false);
  const [voidingPaymentId, setVoidingPaymentId] = useState<string | null>(null);
  const [voidError, setVoidError] = useState<string | null>(null);
  const [voidSuccess, setVoidSuccess] = useState<string | null>(null);
  const [globalStats, setGlobalStats]= useState<PaymentStats | null>(null);
  const [resendingPaymentId, setResendingPaymentId] = useState<string | null>(null);
  const [resendError, setResendError] = useState<string | null>(null);
  const [resendSuccess, setResendSuccess] = useState<string | null>(null);
  // Void modal state
  const [voidModalPayment, setVoidModalPayment] = useState<CancellationPayment | null>(null);
  const [selectedVoidReason, setSelectedVoidReason] = useState<VoidReason | ''>('');
  const [updateClaimStatus, setUpdateClaimStatus] = useState<boolean>(true);
  // Check/audit modal state
  const [checkModalPayment, setCheckModalPayment] = useState<CancellationPayment | null>(null);
  const [checkNotes, setCheckNotes] = useState('');
  const [checkingPaymentId, setCheckingPaymentId] = useState<string | null>(null);
  const [checkError, setCheckError] = useState<string | null>(null);
  const [checkSuccess, setCheckSuccess] = useState<string | null>(null);
  // Actions dropdown state
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
  // View modal state
  const [worldpayResult, setWorldpayResult] = useState<{ paymentId: string; message: string } | null>(null);
  const [viewModalPayment, setViewModalPayment] = useState<CancellationPayment | null>(null);
  // Manual payment recording modal state
  const [manualPayModalPayment, setManualPayModalPayment] = useState<CancellationPayment | null>(null);
  const [manualPayAmount, setManualPayAmount] = useState('');
  const [manualPayIsFinal, setManualPayIsFinal] = useState(true);
  const [manualPayNotes, setManualPayNotes] = useState('');
  const [manualPaySubmitting, setManualPaySubmitting] = useState(false);
  const [manualPayError, setManualPayError] = useState<string | null>(null);
  const [manualPaySuccess, setManualPaySuccess] = useState<string | null>(null);
  // Worldpay phone-payment panel state
  const [worldpayModalPayment, setWorldpayModalPayment] = useState<CancellationPayment | null>(null);
  // Whether the Worldpay phone-payment feature is actually reachable on the
  // backend right now (fails closed: starts false, only flips true once a
  // read-only probe succeeds). Without this, "Take card payment by phone"
  // would appear for every admin/payments-manager the moment the FRONTEND
  // deploys, even before the backend routes exist or WORLDPAY_ENABLED is
  // configured -- clicking it would 404/503 with no warning.
  const [worldpayAvailable, setWorldpayAvailable] = useState(false);
  const [manualPayReconciliationFlag, setManualPayReconciliationFlag] = useState(false);
  // Simple chaser state
  const [chaserStats, setChaserStats] = useState<SimpleChaserStats | null>(null);
  const [chaserStatsFailed, setChaserStatsFailed] = useState(false);
  const [runningChasers, setRunningChasers] = useState(false);
  const [chaserError, setChaserError] = useState<string | null>(null);
  const [chaserSuccess, setChaserSuccess] = useState<string | null>(null);

  const manualAmountValue = useMemo(() => {
    const trimmed = manualAmount.trim();
    if (!trimmed) return Number.NaN;
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : Number.NaN;
  }, [manualAmount]);

  const isManualAmountValid = Number.isFinite(manualAmountValue) && manualAmountValue > 0;

  // The inc-VAT figure staff read is priced by the SAME server-side method
  // that bills the client (stripe_service.calculate_manual_amount, reached
  // via GET /ui/claims/fee-decision-options). Nothing in this file multiplies
  // by a VAT rate: a hardcoded 20% here is precisely how the screen and the
  // charge drift apart the day STRIPE_VAT_RATE moves.
  //
  // `null` means "no server figure right now" -- in flight, failed, or a null
  // `fee` from the endpoint's own pricing failure. All three render the same
  // static ex-VAT warning. They never render nothing: silence was the defect.
  const [manualFeePreview, setManualFeePreview] = useState<RingfenceFee | null>(null);

  useEffect(() => {
    setManualFeePreview(null);
    if (!manualModalOpen || !isManualAmountValid) {
      return;
    }
    let cancelled = false;
    const amount = Number(manualAmountValue.toFixed(2));
    const timer = setTimeout(() => {
      fetchFeeDecisionOptions(0, amount)
        .then((options) => {
          if (!cancelled) setManualFeePreview(options.fee);
        })
        .catch(() => {
          if (!cancelled) setManualFeePreview(null);
        });
    }, MANUAL_PREVIEW_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [manualModalOpen, isManualAmountValid, manualAmountValue]);

  const loadPayments = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetchCancellationPayments({
        status: statusFilter,
        search: appliedSearch || undefined,
        page,
        pageSize
      });
      setPayments(response.payments || []);
      setTotalCount(response.totalCount || 0);
      setTotalPages(response.totalPages || 0);
    } catch (err: any) {
      const message = err?.response?.data?.error || err?.message || 'Failed to load payments';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, appliedSearch, page, pageSize]);

  const loadStats = useCallback(async () => {
    try {
      const stats = await fetchPaymentStats();
      setGlobalStats(stats);
    } catch (err: any) {
      console.error('Failed to load payment stats:', err);
    }
  }, []);

  const loadChaserStats = useCallback(async () => {
    try {
      const stats = await fetchSimpleChaserStats();
      setChaserStats(stats);
      setChaserStatsFailed(false);
    } catch (err: any) {
      console.error('Failed to load chaser stats:', err);
      setChaserStatsFailed(true);
    }
  }, []);

  // Why "Run Chasers" is greyed out, or null when it can be pressed. canRun is
  // only false when the backend says so: an older backend sends no canRun at
  // all (the frontend deploys first), and then its 403 decides. A failed load
  // keeps the button greyed out (fails safe) but says why in plain words.
  const chaserBlockedReason: string | null = chaserStatsFailed
    ? CHASER_STATUS_UNAVAILABLE
    : !chaserStats
      ? 'Loading chaser status…'
      : chaserStats.canRun === false
        ? chaserStats.chaserRunnersLabel || NO_CHASER_PERMISSION
        : null;

  const handleRunChasers = useCallback(async () => {
    setRunningChasers(true);
    setChaserError(null);
    setChaserSuccess(null);

    try {
      const result = await runSimpleChasers(50);

      if (result.error) {
        setChaserError(result.error);
      } else {
        setChaserSuccess(result.message || `Sent ${result.succeeded} of ${result.processed} chasers`);
        // Reload stats and payments after successful run
        await loadChaserStats();
        await loadPayments();

        // Clear success message after 10 seconds
        setTimeout(() => {
          setChaserSuccess(null);
        }, 10000);
      }
    } catch (err: any) {
      const message = err?.response?.data?.error || err?.message || 'Failed to run chasers';
      setChaserError(message);

      // Clear error message after 10 seconds
      setTimeout(() => {
        setChaserError(null);
      }, 10000);
    } finally {
      setRunningChasers(false);
    }
  }, [loadChaserStats, loadPayments]);

  useEffect(() => {
    loadPayments();
    loadStats();
    loadChaserStats();
  }, [loadPayments, loadStats, loadChaserStats]);

  useEffect(() => {
    loadPayments();
  }, [loadPayments]);

  useEffect(() => {
    let cancelled = false;
    checkWorldpayAvailable().then((available) => {
      if (!cancelled) setWorldpayAvailable(available);
    });
    return () => { cancelled = true; };
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (openDropdownId && !(event.target as Element).closest('.actions-dropdown')) {
        setOpenDropdownId(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [openDropdownId]);

  const handleSearchSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setPage(1); // Reset to first page on new search
    setAppliedSearch(searchTerm.trim());
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setAppliedSearch('');
    setPage(1); // Reset to first page
  };

  const handleStatusFilterChange = (newStatus: StatusFilter) => {
    setStatusFilter(newStatus);
    setPage(1); // Reset to first page on filter change
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(1); // Reset to first page when page size changes
  };

  const handleRefresh = () => {
    loadPayments();
  };

  const resetManualForm = useCallback(() => {
    setManualAmount('');
    setManualDescription('');
    setSelectedClaims([]);
    setClaimSearchTerm('');
    setClaimSearchResults([]);
    setClaimSearchError(null);
    setManualError(null);
    setManualSuccess(null);
  }, []);

  const openManualModal = () => {
    resetManualForm();
    setManualModalOpen(true);
  };

  const closeManualModal = () => {
    setManualModalOpen(false);
    resetManualForm();
  };

  const performClaimSearch = useCallback(async () => {
    const term = claimSearchTerm.trim();
    if (!term) {
      setClaimSearchResults([]);
      setClaimSearchError('Enter a search term to find claims.');
      return;
    }

    setSearchingClaims(true);
    setClaimSearchError(null);
    setManualError(null);

    try {
      const response = await fetchClaims(1, 500, term);
      const items = response.items || [];
      setClaimSearchResults(items);
      if (!items.length) {
        setClaimSearchError('No matching claims found.');
      }
    } catch (err: any) {
      const message = err?.response?.data?.error || err?.message || 'Failed to search claims';
      setClaimSearchError(message);
      setClaimSearchResults([]);
    } finally {
      setSearchingClaims(false);
    }
  }, [claimSearchTerm]);

  const handleSelectClaim = (claim: Claim) => {
    // Check if claim is already selected
    if (selectedClaims.some(c => c.id === claim.id)) {
      return;
    }
    setSelectedClaims(prev => [...prev, claim]);
    setClaimSearchResults([]);
    setClaimSearchError(null);
    setManualError(null);
    setManualSuccess(null);
  };

  const handleRemoveClaim = (claimId: string) => {
    setSelectedClaims(prev => prev.filter(c => c.id !== claimId));
    setManualSuccess(null);
  };

  const handleClearAllClaims = () => {
    setSelectedClaims([]);
    setClaimSearchTerm('');
    setManualSuccess(null);
  };

  const handleAddAllClaims = () => {
    setSelectedClaims(prev => {
      const ids = new Set(prev.map(c => c.id));
      return [...prev, ...claimSearchResults.filter(c => !ids.has(c.id))];
    });
    setClaimSearchResults([]);
    setClaimSearchError(null);
    setManualError(null);
    setManualSuccess(null);
  };

  const handleManualPaymentSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setManualError(null);
    setManualSuccess(null);
    setVoidError(null);
    setVoidSuccess(null);

    if (selectedClaims.length === 0) {
      setManualError('Select at least one claim before sending a payment request.');
      return;
    }

    if (!isManualAmountValid) {
      setManualError('Enter a valid amount greater than zero.');
      return;
    }

    try {
      setCreatingManualPayment(true);
      const amountToSend = Number(manualAmountValue.toFixed(2));
      const response = await createManualCancellationPayment({
        claimIds: selectedClaims.map(c => c.id),
        amount: amountToSend,
        description: manualDescription.trim() || undefined
      });

      setManualSuccess(response);
      setManualAmount('');
      setManualDescription('');
      setClaimSearchResults([]);
      await loadPayments();

      // Close modal after brief delay so user sees success
      setTimeout(() => {
        closeManualModal();
      }, 1500);
    } catch (err: any) {
      const message = err?.response?.data?.error || err?.message || 'Failed to create manual payment';
      setManualError(message);
    } finally {
      setCreatingManualPayment(false);
    }
  };

  const summary = useMemo(() => {
    return payments.reduce(
      (acc, payment) => {
        const status = (payment.status || '').toLowerCase();
        const amount = payment.amount || 0;

        acc.totalAmount += amount;

        const isPending = status.includes('pending');
        const isPaid = status === 'completed' || status === 'completed' || status === 'succeeded';

        if (isPending) {
          acc.pending += 1;
          acc.owed += amount;
        }

        if (isPaid) {
          acc.paid += 1;
          acc.paidAmount += amount;
        }

        return acc;
      },
      { totalAmount: 0, pending: 0, paid: 0, owed: 0, paidAmount: 0 }
    );
  }, [payments]);

  // Open void modal
  const openVoidModal = useCallback((payment: CancellationPayment) => {
    setVoidModalPayment(payment);
    setSelectedVoidReason('');
    setUpdateClaimStatus(true);
  }, []);

  // Close void modal
  const closeVoidModal = useCallback(() => {
    setVoidModalPayment(null);
    setSelectedVoidReason('');
    setUpdateClaimStatus(true);
  }, []);

  // Handle void payment confirmation
  const handleVoidPayment = useCallback(async () => {
    if (!voidModalPayment || !selectedVoidReason) return;

    setVoidingPaymentId(voidModalPayment.id);
    setVoidError(null);
    setVoidSuccess(null);

    try {
      const response = await voidCancellationPayment(voidModalPayment.id, {
        voidReason: selectedVoidReason,
        updateClaimStatus
      });
      const message = response?.message || `Payment ${voidModalPayment.id} voided.`;
      setVoidSuccess(message);
      closeVoidModal();
      await loadPayments();
    } catch (err: any) {
      const message = err?.response?.data?.error || err?.message || 'Failed to void payment';
      setVoidError(message);
    } finally {
      setVoidingPaymentId(null);
    }
  }, [voidModalPayment, selectedVoidReason, updateClaimStatus, loadPayments, closeVoidModal]);

  // Open check modal
  const openCheckModal = useCallback((payment: CancellationPayment) => {
    setCheckModalPayment(payment);
    setCheckNotes('');
    setCheckError(null);
  }, []);

  // Close check modal
  const closeCheckModal = useCallback(() => {
    setCheckModalPayment(null);
    setCheckNotes('');
    setCheckError(null);
  }, []);

  // Open manual payment modal
  const openManualPayModal = useCallback((payment: CancellationPayment) => {
    setManualPayModalPayment(payment);
    const previouslyPaid = payment.manualPayment?.amountPaid || 0;
    const remaining = Math.max(0, (payment.amount || 0) - previouslyPaid);
    setManualPayAmount(remaining > 0 ? remaining.toFixed(2) : (payment.amount?.toString() || ''));
    setManualPayIsFinal(true);
    setManualPayNotes('');
    setManualPayError(null);
    setManualPaySuccess(null);
  }, []);

  // Close manual payment modal
  const closeManualPayModal = useCallback(() => {
    setManualPayModalPayment(null);
    setManualPayAmount('');
    setManualPayNotes('');
    setManualPaySubmitting(false);
  }, []);

  // Handle manual payment submission
  const handleManualPaySubmit = useCallback(async () => {
    if (!manualPayModalPayment) return;

    const amountPaid = parseFloat(manualPayAmount);
    if (!Number.isFinite(amountPaid) || amountPaid <= 0) return;

    setManualPaySubmitting(true);

    setManualPayError(null);
    setManualPaySuccess(null);
    setManualPayReconciliationFlag(false);

    try {
      const response = await markManualPayment(manualPayModalPayment.id, {
        amountPaid,
        isFinal: manualPayIsFinal,
        notes: manualPayNotes.trim() || undefined
      });
      // Final fix-wave C2: an overpayment is recorded but deliberately NOT
      // auto-completed -- the banner below renders this distinctly (amber
      // "warning", not the routine green "success") so staff don't read it
      // as an ordinary confirmation.
      setManualPayReconciliationFlag(Boolean(response?.reconciliationFlag));
      setManualPaySuccess(response?.message || 'Manual payment recorded successfully.');
      closeManualPayModal();
      await loadPayments();

      setTimeout(() => {
        setManualPaySuccess(null);
        setManualPayReconciliationFlag(false);
      }, response?.reconciliationFlag ? 15000 : 5000);
    } catch (err: any) {
      const message = err?.response?.data?.error || err?.message || 'Failed to record manual payment';
      setManualPayError(message);
    } finally {
      setManualPaySubmitting(false);
    }
  }, [manualPayModalPayment, manualPayAmount, manualPayIsFinal, manualPayNotes, loadPayments, closeManualPayModal]);

  // Open view modal
  const openViewModal = useCallback((payment: CancellationPayment) => {
    setViewModalPayment(payment);
    setOpenDropdownId(null);
  }, []);

  // Close view modal
  const closeViewModal = useCallback(() => {
    setViewModalPayment(null);
  }, []);

  // Handle check payment confirmation
  const handleCheckPayment = useCallback(async () => {
    if (!checkModalPayment) return;

    setCheckingPaymentId(checkModalPayment.id);
    setCheckError(null);
    setCheckSuccess(null);

    try {
      const response = await checkCancellationPayment(checkModalPayment.id, {
        notes: checkNotes.trim() || undefined
      });
      const message = response?.message || `Payment ${checkModalPayment.id} marked as checked.`;
      setCheckSuccess(message);
      closeCheckModal();
      await loadPayments();

      // Clear success message after 5 seconds
      setTimeout(() => {
        setCheckSuccess(null);
      }, 5000);
    } catch (err: any) {
      const message = err?.response?.data?.error || err?.message || 'Failed to mark payment as checked';
      setCheckError(message);
    } finally {
      setCheckingPaymentId(null);
    }
  }, [checkModalPayment, checkNotes, loadPayments, closeCheckModal]);

  const handleResendPaymentLink = useCallback(async (payment: CancellationPayment) => {
    const status = (payment.status || '').toLowerCase();
    const canResend = ['pending', 'pending_payment', 'open', 'expired'].includes(status);
    if (!canResend) return;

    setResendingPaymentId(payment.id);
    setResendError(null);
    setResendSuccess(null);

    try {
      const response = await resendPaymentLink(payment.id);
      const message = response?.message || `Payment link resent to ${payment.clientEmail}`;
      setResendSuccess(message);

      // Clear success message after 5 seconds
      setTimeout(() => {
        setResendSuccess(null);
      }, 5000);
    } catch (err: any) {
      const message = err?.response?.data?.error || err?.message || 'Failed to resend payment link';
      setResendError(message);

      // Clear error message after 5 seconds
      setTimeout(() => {
        setResendError(null);
      }, 5000);
    } finally {
      setResendingPaymentId(null);
    }
  }, []);

  return (
    <div className="admin-payments-container">
      <div className="admin-payments-header">
        <div className="admin-payments-header-text">
          <h2>Cancellation Payments</h2>
          <p style={{ margin: 0, color: '#6b7280' }}>
            Review Stripe payment requests generated for claim cancellations. Use the filters below to refine the list.
          </p>
        </div>
        <div className="admin-payments-header-actions">
          {/* Simple Chaser Section */}
          <div className="simple-chaser-section">
            <button
              type="button"
              className="run-chasers-button"
              onClick={handleRunChasers}
              disabled={runningChasers || chaserBlockedReason !== null}
              title={chaserBlockedReason ?? 'Send payment reminders to oldest 50 unpaid payments'}
            >
              <span className="chaser-icon">📧</span>
              <span>{runningChasers ? 'Running...' : 'Run Chasers (50)'}</span>
            </button>
            {chaserStatsFailed ? (
              <div className="chaser-stats" data-testid="chaser-status-unavailable">{CHASER_STATUS_UNAVAILABLE}</div>
            ) : chaserStats?.canRun === false && (
              <div className="chaser-stats" data-testid="chaser-runners-only">{chaserBlockedReason}</div>
            )}
            {chaserStats && (
              <div className="chaser-stats">
                <span className="chaser-stat">
                  Last run: {chaserStats.lastRunAt
                    ? formatDate(chaserStats.lastRunAt)
                    : 'Never'}
                </span>
                <span className="chaser-stat">
                  Total chased: {chaserStats.totalChased}
                </span>
              </div>
            )}
          </div>
          <button type="button" className="manual-trigger-button" onClick={openManualModal}>
            <span className="manual-trigger-icon">＋</span>
            <span>New manual payment</span>
          </button>
        </div>
      </div>

      {manualModalOpen && (
        <Modal onClose={closeManualModal}>
          <div className="manual-payment-card manual-modal-inner">
            <div className="manual-card-header">
              <h3>Send Manual Payment Request</h3>
              <p>
                Generate a Stripe payment link for ad-hoc charges. Enter the net amount (excluding VAT) and choose the claim — the server prices the VAT and shows you the total the client will actually be charged.
              </p>
            </div>

            {manualError && (
              <div className="manual-feedback error">
                {manualError}
              </div>
            )}
            {manualSuccess && (
              <div className="manual-feedback success">
                Payment link created for <strong>{manualSuccess.clientEmail}</strong>.
                {' '}
                <a href={manualSuccess.paymentLinkUrl} target="_blank" rel="noopener noreferrer">
                  Open payment link
                </a>
              </div>
            )}

            <form className="manual-payment-form" onSubmit={handleManualPaymentSubmit}>
              <div className="manual-field">
                <label>
                  Claims
                  {selectedClaims.length > 0 && (
                    <span className="claims-count-badge">{selectedClaims.length} selected</span>
                  )}
                </label>

                {/* Selected claims list */}
                {selectedClaims.length > 0 && (
                  <div className="selected-claims-list">
                    {selectedClaims.map((claim) => (
                      <div key={claim.id} className="selected-claim-item">
                        <div className="selected-claim-info">
                          <span className="claim-id">{claim.id}</span>
                          <span className="claim-name">
                            {[claim.firstName, claim.lastName].filter(Boolean).join(' ') || 'Unnamed'}
                          </span>
                          {claim.email && <span className="claim-email">{claim.email}</span>}
                        </div>
                        <button
                          type="button"
                          className="remove-claim-btn"
                          onClick={() => handleRemoveClaim(claim.id)}
                          title="Remove this claim"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      className="clear-all-claims"
                      onClick={handleClearAllClaims}
                    >
                      Clear all
                    </button>
                  </div>
                )}

                {/* Claim search */}
                <div className="claim-search">
                  <div className="claim-search-controls">
                    <input
                      type="text"
                      placeholder="Search by claim ID, name, or email to add"
                      value={claimSearchTerm}
                      onChange={(e) => setClaimSearchTerm(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          performClaimSearch();
                        }
                      }}
                    />
                    <button
                      type="button"
                      className="secondary"
                      onClick={performClaimSearch}
                      disabled={searchingClaims}
                    >
                      {searchingClaims ? 'Searching…' : 'Search'}
                    </button>
                  </div>
                  {claimSearchError && (
                    <div className="claim-search-feedback">{claimSearchError}</div>
                  )}
                  {claimSearchResults.length > 0 && (
                    <button
                      type="button"
                      className="add-all-claims"
                      onClick={handleAddAllClaims}
                      disabled={claimSearchResults.length === 0}
                    >
                      Add all Files ({claimSearchResults.length})
                    </button>
                  )}
                  {claimSearchResults.length > 0 && (
                    <ul className="claim-search-results">
                      {claimSearchResults.map((claim) => {
                        const isAlreadySelected = selectedClaims.some(c => c.id === claim.id);
                        return (
                          <li key={claim.id} className={isAlreadySelected ? 'already-selected' : ''}>
                            <button
                              type="button"
                              onClick={() => handleSelectClaim(claim)}
                              disabled={isAlreadySelected}
                            >
                              <span className="claim-id">{claim.id}</span>
                              <span className="claim-name">
                                {[claim.firstName, claim.lastName].filter(Boolean).join(' ') || 'Unnamed client'}
                              </span>
                              {claim.email && <span className="claim-email">{claim.email}</span>}
                              {isAlreadySelected && <span className="already-added-label">Added</span>}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              </div>

              <div className="manual-field">
                <label htmlFor="manual-amount">Amount (ex VAT)</label>
                <input
                  id="manual-amount"
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  value={manualAmount}
                  onChange={(e) => setManualAmount(e.target.value)}
                />
                <small>Enter the amount BEFORE VAT. The total the client pays is priced by the server and shown below.</small>
              </div>

              {manualFeePreview ? (
                <div className="manual-fee-preview" data-testid="manual-fee-preview">
                  Client will be charged{' '}
                  <strong>{currencyFormatter.format(manualFeePreview.totalAmountPounds)}</strong>
                  {' ('}
                  {currencyFormatter.format(manualFeePreview.baseAmountPounds)}
                  {' + '}
                  {currencyFormatter.format(manualFeePreview.vatAmountPounds)}
                  {' VAT)'}
                </div>
              ) : (
                <div
                  className="manual-fee-preview manual-fee-preview-warning"
                  data-testid="manual-fee-preview"
                >
                  This is the amount BEFORE VAT — the client will be charged this figure plus VAT.
                </div>
              )}

              <div className="manual-field">
                <label htmlFor="manual-description">Notes (optional)</label>
                <input
                  id="manual-description"
                  type="text"
                  placeholder="Reason for manual charge"
                  value={manualDescription}
                  onChange={(e) => setManualDescription(e.target.value)}
                />
              </div>

              <div className="manual-actions">
                <button
                  type="submit"
                  className="primary"
                  disabled={selectedClaims.length === 0 || !isManualAmountValid || creatingManualPayment}
                >
                  {creatingManualPayment ? 'Sending…' : `Send payment request${selectedClaims.length > 1 ? ` (${selectedClaims.length} claims)` : ''}`}
                </button>
              </div>
            </form>
          </div>
        </Modal>
      )}

      {/* Void Payment Modal */}
      {voidModalPayment && (
        <Modal onClose={closeVoidModal}>
          <div className="void-modal-inner">
            <h3>Void Payment Request</h3>
            <p className="void-modal-warning">
              This will deactivate the payment link. The payment record will be preserved for audit purposes.
            </p>

            <div className="void-modal-details">
              <div className="void-detail-row">
                <span className="void-detail-label">Payment ID:</span>
                <code>{voidModalPayment.id}</code>
              </div>
              <div className="void-detail-row">
                <span className="void-detail-label">Client:</span>
                <span>{voidModalPayment.clientName || voidModalPayment.clientEmail}</span>
              </div>
              <div className="void-detail-row">
                <span className="void-detail-label">Amount:</span>
                <span>{currencyFormatter.format(voidModalPayment.amount || 0)}</span>
              </div>
            </div>

            <div className="void-form-field">
              <label htmlFor="void-reason">Reason for voiding *</label>
              <select
                id="void-reason"
                value={selectedVoidReason}
                onChange={(e) => setSelectedVoidReason(e.target.value as VoidReason)}
                required
              >
                <option value="">Select a reason...</option>
                {VOID_REASONS.map((reason) => (
                  <option key={reason} value={reason}>{reason}</option>
                ))}
              </select>
            </div>

            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginTop: '12px' }}>
              <input
                type="checkbox"
                id="update-claim-status"
                checked={updateClaimStatus}
                onChange={(e) => setUpdateClaimStatus(e.target.checked)}
                style={{ marginTop: '3px' }}
              />
              <label htmlFor="update-claim-status" style={{ fontSize: '14px', color: '#374151' }}>
                Update file to show no monies outstanding
              </label>
            </div>

            <div className="void-modal-actions">
              <button type="button" className="secondary" onClick={closeVoidModal}>
                Cancel
              </button>
              <button
                type="button"
                className="danger"
                onClick={handleVoidPayment}
                disabled={!selectedVoidReason || voidingPaymentId === voidModalPayment.id}
              >
                {voidingPaymentId === voidModalPayment.id ? 'Voiding...' : 'Void Payment'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {voidError && (
        <div className="admin-payments-feedback error">{voidError}</div>
      )}
      {voidSuccess && (
        <div className="admin-payments-feedback success">{voidSuccess}</div>
      )}

      {resendError && (
        <div className="admin-payments-feedback error">{resendError}</div>
      )}
      {resendSuccess && (
        <div className="admin-payments-feedback success">{resendSuccess}</div>
      )}

      {checkError && (
        <div className="admin-payments-feedback error">{checkError}</div>
      )}
      {checkSuccess && (
        <div className="admin-payments-feedback success">{checkSuccess}</div>
      )}

      {chaserError && (
        <div className="admin-payments-feedback error">{chaserError}</div>
      )}
      {chaserSuccess && (
        <div className="admin-payments-feedback success">{chaserSuccess}</div>
      )}
      {manualPayError && !manualPayModalPayment && (
        <div className="admin-payments-feedback error">{manualPayError}</div>
      )}
      {manualPaySuccess && (
        <div
          className={`admin-payments-feedback ${manualPayReconciliationFlag ? 'warning' : 'success'}`}
          role={manualPayReconciliationFlag ? 'alert' : undefined}
        >
          {manualPayReconciliationFlag && <strong>Needs reconciliation — </strong>}
          {manualPaySuccess}
        </div>
      )}

      {/* Check/Audit Payment Modal */}
      {checkModalPayment && (
        <Modal onClose={closeCheckModal}>
          <div className="check-modal-inner">
            <h3>{checkModalPayment.isChecked ? 'Recheck Payment' : 'Mark Payment as Checked'}</h3>
            <p className="check-modal-info">
              {checkModalPayment.isChecked
                ? 'Record another audit check for this payment. Previous checks are preserved in the history.'
                : 'Record that this payment has been audited. This action is tracked with your email and timestamp.'}
            </p>

            <div className="check-modal-details">
              <div className="check-detail-row">
                <span className="check-detail-label">Payment ID:</span>
                <code>{checkModalPayment.id}</code>
              </div>
              <div className="check-detail-row">
                <span className="check-detail-label">Client:</span>
                <span>{checkModalPayment.clientName || checkModalPayment.clientEmail}</span>
              </div>
              <div className="check-detail-row">
                <span className="check-detail-label">Amount:</span>
                <span>{currencyFormatter.format(checkModalPayment.amount || 0)}</span>
              </div>
              <div className="check-detail-row">
                <span className="check-detail-label">Status:</span>
                <span className={`status-pill ${checkModalPayment.status || 'pending'}`}>
                  {checkModalPayment.status || 'pending'}
                </span>
              </div>
              {checkModalPayment.isChecked && (
                <div className="check-detail-row previously-checked">
                  <span className="check-detail-label">Last Checked:</span>
                  <span>
                    {formatDate(checkModalPayment.checkedAt)} by {checkModalPayment.checkedBy}
                  </span>
                </div>
              )}
              {checkModalPayment.checkHistory && checkModalPayment.checkHistory.length > 0 && (
                <div className="check-detail-row">
                  <span className="check-detail-label">Total Checks:</span>
                  <span>{checkModalPayment.checkHistory.length}</span>
                </div>
              )}
            </div>

            <div className="check-form-field">
              <label htmlFor="check-notes">Notes (optional)</label>
              <textarea
                id="check-notes"
                value={checkNotes}
                onChange={(e) => setCheckNotes(e.target.value)}
                placeholder="Add any notes about this audit check..."
                rows={3}
              />
            </div>

            <div className="check-modal-actions">
              <button type="button" className="secondary" onClick={closeCheckModal}>
                Cancel
              </button>
              <button
                type="button"
                className="primary"
                onClick={handleCheckPayment}
                disabled={checkingPaymentId === checkModalPayment.id}
              >
                {checkingPaymentId === checkModalPayment.id
                  ? 'Saving...'
                  : checkModalPayment.isChecked
                    ? 'Add Recheck'
                    : 'Mark as Checked'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Manual Payment Recording Modal */}
      {manualPayModalPayment && (
        <Modal onClose={closeManualPayModal}>
          <div className="void-modal-content">
            <h3>Record Manual Payment</h3>
            <p style={{ color: '#6b7280', fontSize: '14px', marginBottom: '16px' }}>
              Record a payment received outside of Stripe (e.g. bank transfer). This will deactivate the Stripe payment link.
            </p>

            {manualPayError && (
              <div className="admin-payments-feedback error" style={{ marginBottom: '12px' }}>{manualPayError}</div>
            )}

            <div className="void-detail-row">
              <span className="void-detail-label">Payment ID:</span>
              <code>{manualPayModalPayment.id}</code>
            </div>
            <div className="void-detail-row">
              <span className="void-detail-label">Client:</span>
              <span>{manualPayModalPayment.clientName || manualPayModalPayment.clientEmail}</span>
            </div>
            <div className="void-detail-row">
              <span className="void-detail-label">Expected Amount:</span>
              <span>{currencyFormatter.format(manualPayModalPayment.amount || 0)}</span>
            </div>
            {manualPayModalPayment.manualPayment && manualPayModalPayment.manualPayment.amountPaid > 0 && (
              <div className="void-detail-row">
                <span className="void-detail-label">Already Paid:</span>
                <span style={{ color: '#059669', fontWeight: 500 }}>{currencyFormatter.format(manualPayModalPayment.manualPayment.amountPaid)}</span>
              </div>
            )}

            <div className="check-form-field" style={{ marginTop: '16px' }}>
              <label htmlFor="manual-pay-amount">Amount Received</label>
              <div style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#6b7280', fontSize: '14px' }}>£</span>
                <input
                  id="manual-pay-amount"
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={manualPayAmount}
                  onChange={(e) => setManualPayAmount(e.target.value)}
                  style={{ paddingLeft: '28px', width: '100%', padding: '8px 8px 8px 28px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                />
              </div>
            </div>

            <div className="check-form-field" style={{ marginTop: '12px' }}>
              <label>Payment Type</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '4px' }}>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', cursor: 'pointer', fontSize: '14px' }}>
                  <input
                    type="radio"
                    name="manualPayType"
                    checked={manualPayIsFinal}
                    onChange={() => setManualPayIsFinal(true)}
                    style={{ marginTop: '2px' }}
                  />
                  <div>
                    <div style={{ fontWeight: 500 }}>Final payment</div>
                    <div style={{ color: '#6b7280', fontSize: '12px' }}>Full amount or agreed settlement — no further payment expected</div>
                  </div>
                </label>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', cursor: 'pointer', fontSize: '14px' }}>
                  <input
                    type="radio"
                    name="manualPayType"
                    checked={!manualPayIsFinal}
                    onChange={() => setManualPayIsFinal(false)}
                    style={{ marginTop: '2px' }}
                  />
                  <div>
                    <div style={{ fontWeight: 500 }}>Partial payment</div>
                    <div style={{ color: '#6b7280', fontSize: '12px' }}>Remainder still outstanding — payment will show as partially paid</div>
                  </div>
                </label>
              </div>
            </div>

            {!manualPayIsFinal && manualPayAmount && parseFloat(manualPayAmount) > 0 && (() => {
              const previouslyPaid = manualPayModalPayment.manualPayment?.amountPaid || 0;
              const outstanding = Math.max(0, (manualPayModalPayment.amount || 0) - previouslyPaid - parseFloat(manualPayAmount));
              return (
                <div style={{ marginTop: '8px', padding: '8px 12px', backgroundColor: '#fef3c7', borderRadius: '6px', fontSize: '13px', color: '#92400e' }}>
                  Outstanding balance: {currencyFormatter.format(outstanding)}
                </div>
              );
            })()}

            <div className="check-form-field" style={{ marginTop: '12px' }}>
              <label htmlFor="manual-pay-notes">Notes (optional)</label>
              <textarea
                id="manual-pay-notes"
                value={manualPayNotes}
                onChange={(e) => setManualPayNotes(e.target.value)}
                placeholder="e.g. Bank transfer ref ABC123, received 03/03/2026..."
                rows={3}
              />
            </div>

            <div className="void-modal-actions">
              <button type="button" className="secondary" onClick={closeManualPayModal}>
                Cancel
              </button>
              <button
                type="button"
                className="primary"
                onClick={handleManualPaySubmit}
                disabled={manualPaySubmitting || !manualPayAmount || parseFloat(manualPayAmount) <= 0}
                style={{ backgroundColor: '#d97706' }}
              >
                {manualPaySubmitting ? 'Recording...' : 'Record Payment'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {worldpayModalPayment && (
        <Modal onClose={() => setWorldpayModalPayment(null)}>
          <WorldpayPhonePaymentPanel
            payment={worldpayModalPayment}
            onClose={() => setWorldpayModalPayment(null)}
            onSettled={async (result) => {
              const payment = worldpayModalPayment;
              setWorldpayModalPayment(null);
              setWorldpayResult({ paymentId: payment.id, message: result?.environment === 'try'
                ? 'Test payment successful. This was a Try payment; the client balance and receipts have not changed.'
                : 'Payment successful and recorded in PCP.' });
              setViewModalPayment(payment);
              try {
                const fresh = await getCheckoutPayment(payment.id);
                setViewModalPayment(current => current?.id === payment.id ? fresh : current);
              } catch {
                setWorldpayResult({ paymentId: payment.id, message: 'Payment successful, but the latest payment details could not be loaded. Refresh the page; do not take payment again.' });
              }
              await Promise.all([loadPayments(), loadStats()]);
            }}
          />
        </Modal>
      )}

      {/* Payment Details View Modal */}
      {viewModalPayment && (() => {
        const payment = viewModalPayment;
        const status = (payment.status || '').toLowerCase();
        const paymentTypeRaw = payment.paymentType || (payment.isManual ? 'manual' : undefined);
        const paymentType = paymentTypeRaw ? paymentTypeRaw.replace(/_/g, ' ') : 'cancellation';
        const isReinstated = Boolean(payment.reinstatedAt);
        const isVoided = status === 'voided';
        const normalizedStatus = status || 'pending';
        // #591: a failed payment can be voided too (e.g. payment plan agreed); the backend already allows it.
        const canVoid = ['pending', 'pending_payment', 'open', 'expired', 'failed'].includes(normalizedStatus);
        const canResend = ['pending', 'pending_payment', 'open', 'expired'].includes(normalizedStatus);
        const canManualPay = ['pending', 'partial'].includes(normalizedStatus);

        return (
          <Modal onClose={closeViewModal}>
            <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              {worldpayResult?.paymentId === payment.id && <div role="status" className="admin-payments-feedback success">{worldpayResult.message}</div>}
              {/* Header */}
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">Payment Details</h3>
                  <button
                    type="button"
                    onClick={closeViewModal}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="px-6 py-4 space-y-6">
                {/* Payment Info Section */}
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wide">Payment Information</h4>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">Payment ID</span>
                      <div className="flex items-center gap-2">
                        <code className="text-sm font-mono text-gray-700 bg-gray-100 px-2 py-1 rounded">{payment.id}</code>
                        <button
                          type="button"
                          onClick={() => {
                            navigator.clipboard.writeText(payment.id);
                          }}
                          className="text-gray-400 hover:text-gray-600 transition-colors"
                          title="Copy Payment ID"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                        </button>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">Created</span>
                      <span className="text-sm text-gray-900">{formatDate(payment.createdAt)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">Type</span>
                      <span className="text-sm text-gray-900 capitalize">{paymentType}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">Number of Claims</span>
                      <span className="text-sm text-gray-900">{payment.claimIds?.length || 0}</span>
                    </div>
                  </div>
                </div>

                {/* Client Info Section */}
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wide">Client Information</h4>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">Name</span>
                      <span className="text-sm text-gray-900">{payment.clientName || '---'}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">Email</span>
                      <span className="text-sm text-gray-900">{payment.clientEmail}</span>
                    </div>
                  </div>
                </div>

                {/* Status Section */}
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wide">Status</h4>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">Payment Status</span>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={`status-pill ${status || 'pending'}`}>
                          {status || 'pending'}
                        </span>
                        {isReinstated && (
                          <span className="status-pill reinstated">reinstated</span>
                        )}
                        {isVoided && (
                          <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded bg-gray-200 text-gray-700">voided</span>
                        )}
                      </div>
                    </div>
                    {payment.isChecked && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-500">Audit Status</span>
                        <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded bg-green-100 text-green-700">
                          Checked
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Amount Section */}
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wide">Amount</h4>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                    {payment.baseAmount !== undefined && payment.vatAmount !== undefined ? (
                      <>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-500">Base Amount (ex VAT)</span>
                          <span className="text-sm text-gray-900">{currencyFormatter.format(payment.baseAmount || 0)}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-500">VAT</span>
                          <span className="text-sm text-gray-900">{currencyFormatter.format(payment.vatAmount || 0)}</span>
                        </div>
                        <div className="flex items-center justify-between border-t border-gray-200 pt-2">
                          <span className="text-sm font-medium text-gray-700">Total</span>
                          <span className={`text-sm font-semibold ${isVoided ? 'text-gray-400 line-through' : 'text-gray-900'}`}>
                            {currencyFormatter.format(payment.amount || 0)}
                          </span>
                        </div>
                      </>
                    ) : (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-500">Amount</span>
                        <span className={`text-sm font-semibold ${isVoided ? 'text-gray-400 line-through' : 'text-gray-900'}`}>
                          {currencyFormatter.format(payment.amount || 0)}
                        </span>
                      </div>
                    )}
                    {payment.paidAt && (
                      <div className="flex items-center justify-between border-t border-gray-200 pt-2">
                        <span className="text-sm text-gray-500">Paid At</span>
                        <span className="text-sm text-green-600 font-medium">{formatDate(payment.paidAt)}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Manual Payment Info Section -- final fix-wave (Important
                    1): payment.manualPayment is written for EVERY
                    settlement channel (bank transfer, Worldpay staff-
                    confirm, and Stripe's own webhook all funnel through
                    settle_payment's shared amount/ledger bookkeeping), so
                    its presence alone doesn't mean a human recorded this
                    by hand. A Stripe card payment reads as an online
                    payment via the generic Payment Status/Amount/Paid At
                    sections above -- gated out here so it no longer also
                    shows this amber "manually recorded" panel with
                    "Recorded By: stripe_webhook". Worldpay is untouched:
                    it gets its own dedicated indigo section below AND
                    still carries manualPayment (same shared bookkeeping),
                    so both continue to render together as before. */}
                {payment.manualPayment && payment.paymentMethod !== 'stripe' && (
                  <div className="space-y-3">
                    <h4 className="text-sm font-medium text-amber-600 uppercase tracking-wide">Manual Payment Details</h4>
                    <div className="bg-amber-50 rounded-lg p-4 space-y-3 border border-amber-100">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-amber-600">Amount Received</span>
                        <span className="text-sm text-amber-700 font-semibold">{currencyFormatter.format(payment.manualPayment.amountPaid)}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-amber-600">Expected Amount</span>
                        <span className="text-sm text-amber-700">{currencyFormatter.format(payment.manualPayment.expectedAmount)}</span>
                      </div>
                      {/* #621: a final (incl. agreed reduced) payment owes nothing. */}
                      {amountStillOwed(payment.manualPayment) > 0 && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-amber-600">Outstanding Balance</span>
                          <span className="text-sm text-red-600 font-medium">{currencyFormatter.format(amountStillOwed(payment.manualPayment))}</span>
                        </div>
                      )}
                      <div className="flex items-center justify-between border-t border-amber-200 pt-2">
                        <span className="text-sm text-amber-600">Payment Type</span>
                        <span className="text-sm text-amber-700 font-medium">{payment.manualPayment.isFinal ? 'Final / Settled' : 'Partial'}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-amber-600">Recorded By</span>
                        <span className="text-sm text-amber-700">{payment.manualPayment.recordedBy}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-amber-600">Recorded At</span>
                        <span className="text-sm text-amber-700">{formatDate(payment.manualPayment.recordedAt)}</span>
                      </div>
                      {payment.manualPayment.notes && (
                        <div className="border-t border-amber-200 pt-2">
                          <span className="text-sm text-amber-600 block mb-1">Notes</span>
                          <p className="text-sm text-amber-700 italic">"{payment.manualPayment.notes}"</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Worldpay Phone-Payment Section -- worldpayConfirmation was typed
                    in payments.ts but never displayed anywhere; staff had no way
                    to see whether a phone payment was only staff-confirmed,
                    independently verified by Worldpay's webhook, or recorded
                    from the webhook alone with no staff confirmation at all. */}
                {payment.worldpayConfirmation && (
                  <div className="space-y-3">
                    <h4 className="text-sm font-medium text-indigo-600 uppercase tracking-wide">Worldpay Phone Payment</h4>
                    <div className="bg-indigo-50 rounded-lg p-4 space-y-3 border border-indigo-100">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-indigo-600">Confirmation Status</span>
                        <WorldpayConfirmationBadge confirmation={payment.worldpayConfirmation} />
                      </div>
                      {payment.worldpayConfirmation.confirmedBy && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-indigo-600">Staff Confirmed By</span>
                          <span className="text-sm text-indigo-700">{payment.worldpayConfirmation.confirmedBy}</span>
                        </div>
                      )}
                      {payment.worldpayConfirmation.confirmedAt && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-indigo-600">Staff Confirmed At</span>
                          <span className="text-sm text-indigo-700">{formatDate(payment.worldpayConfirmation.confirmedAt)}</span>
                        </div>
                      )}
                      {payment.worldpayConfirmation.verifiedAt && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-indigo-600">Worldpay Verified At</span>
                          <span className="text-sm text-indigo-700">{formatDate(payment.worldpayConfirmation.verifiedAt)}</span>
                        </div>
                      )}
                      {payment.worldpayReference && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-indigo-600">Reference</span>
                          <code className="text-sm text-indigo-700">{payment.worldpayReference}</code>
                        </div>
                      )}
                      {payment.worldpayTransactionId && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-indigo-600">Worldpay Transaction ID</span>
                          <code className="text-sm text-indigo-700">{payment.worldpayTransactionId}</code>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Declined/Failed Worldpay Attempts -- worldpayEvents was
                    written by append_worldpay_event_log for every
                    non-success event (e.g. a declined card) matched to
                    THIS payment, but never displayed; staff clicking
                    "payment taken" had no way to see the card was declined
                    on an earlier attempt first. */}
                {payment.worldpayEvents && payment.worldpayEvents.length > 0 && (
                  <div className="space-y-3">
                    <h4 className="text-sm font-medium text-red-500 uppercase tracking-wide">
                      Declined / Failed Attempts ({payment.worldpayEvents.length})
                    </h4>
                    <div className="bg-red-50 rounded-lg p-4 space-y-2 border border-red-100">
                      {[...payment.worldpayEvents].reverse().map((event, index) => (
                        <div
                          key={event.eventId || index}
                          className="flex items-center justify-between text-sm border-b border-red-100 last:border-b-0 pb-2 last:pb-0"
                        >
                          <span className="text-red-700">
                            {event.eventType || 'declined'} —{' '}
                            {event.amount == null ? 'Amount unavailable' : currencyFormatter.format(event.amount)}
                          </span>
                          <span className="text-red-600">{formatDate(event.receivedAt)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Payment Link Section */}
                {payment.paymentLinkUrl && !isVoided && (
                  <div className="space-y-3">
                    <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wide">Payment Link</h4>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <a
                        href={payment.paymentLinkUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-blue-600 hover:text-blue-800 hover:underline break-all"
                      >
                        {payment.paymentLinkUrl}
                      </a>
                    </div>
                  </div>
                )}

                {/* Void Info Section */}
                {isVoided && (
                  <div className="space-y-3">
                    <h4 className="text-sm font-medium text-red-500 uppercase tracking-wide">Void Information</h4>
                    <div className="bg-red-50 rounded-lg p-4 space-y-3 border border-red-100">
                      {payment.voidReason && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-red-600">Reason</span>
                          <span className="text-sm text-red-700 font-medium">{payment.voidReason}</span>
                        </div>
                      )}
                      {payment.voidedBy && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-red-600">Voided By</span>
                          <span className="text-sm text-red-700">{payment.voidedBy}</span>
                        </div>
                      )}
                      {payment.voidedAt && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-red-600">Voided At</span>
                          <span className="text-sm text-red-700">{formatDate(payment.voidedAt)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Check History Section */}
                {payment.checkHistory && payment.checkHistory.length > 0 && (
                  <div className="space-y-3">
                    <h4 className="text-sm font-medium text-green-600 uppercase tracking-wide">
                      Audit History ({payment.checkHistory.length} {payment.checkHistory.length === 1 ? 'check' : 'checks'})
                    </h4>
                    <div className="bg-green-50 rounded-lg p-4 space-y-3 border border-green-100">
                      {[...payment.checkHistory].reverse().map((check, index) => (
                        <div
                          key={index}
                          className={`${index > 0 ? 'border-t border-green-100 pt-3' : ''}`}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-medium text-green-700">{check.checkedBy}</span>
                            <span className="text-xs text-green-600">{formatDate(check.checkedAt)}</span>
                          </div>
                          {check.notes ? (
                            <p className="text-sm text-green-600 italic">"{check.notes}"</p>
                          ) : (
                            <p className="text-sm text-green-500 italic">(no notes)</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Legacy check info for payments without checkHistory array */}
                {payment.isChecked && (!payment.checkHistory || payment.checkHistory.length === 0) && (
                  <div className="space-y-3">
                    <h4 className="text-sm font-medium text-green-600 uppercase tracking-wide">Audit Information</h4>
                    <div className="bg-green-50 rounded-lg p-4 space-y-3 border border-green-100">
                      {payment.checkedBy && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-green-600">Checked By</span>
                          <span className="text-sm text-green-700">{payment.checkedBy}</span>
                        </div>
                      )}
                      {payment.checkedAt && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-green-600">Checked At</span>
                          <span className="text-sm text-green-700">{formatDate(payment.checkedAt)}</span>
                        </div>
                      )}
                      {payment.checkNotes && (
                        <div className="border-t border-green-100 pt-2">
                          <span className="text-sm text-green-600 block mb-1">Notes</span>
                          <p className="text-sm text-green-700">{payment.checkNotes}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Actions Footer */}
              <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
                <div className="flex flex-wrap items-center justify-end gap-3">
                  {/* Always show check/recheck button */}
                  <button
                    type="button"
                    onClick={() => {
                      closeViewModal();
                      openCheckModal(payment);
                    }}
                    className={`inline-flex items-center px-4 py-2 text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-1 transition-colors ${
                      payment.isChecked
                        ? 'text-green-700 bg-green-50 border border-green-200 hover:bg-green-100 focus:ring-green-500'
                        : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 focus:ring-blue-500'
                    }`}
                  >
                    {payment.isChecked ? (
                      <>
                        <svg className="w-4 h-4 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                        </svg>
                        Recheck Payment
                      </>
                    ) : (
                      'Mark as Checked'
                    )}
                  </button>

                  {canResend && (
                    <button
                      type="button"
                      onClick={() => {
                        handleResendPaymentLink(payment);
                      }}
                      disabled={resendingPaymentId === payment.id}
                      className="inline-flex items-center px-4 py-2 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 transition-colors disabled:opacity-50"
                    >
                      {resendingPaymentId === payment.id ? 'Resending...' : 'Resend Link'}
                    </button>
                  )}

                  {payment.paymentLinkUrl && !isVoided && (
                    <a
                      href={payment.paymentLinkUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 transition-colors"
                    >
                      Open Payment Link
                    </a>
                  )}

                  {canManualPay && (
                    <button
                      type="button"
                      onClick={() => {
                        closeViewModal();
                        openManualPayModal(payment);
                      }}
                      className="inline-flex items-center px-4 py-2 text-sm font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-md hover:bg-amber-100 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-1 transition-colors"
                    >
                      Mark as Manually Paid
                    </button>
                  )}

                  {canManualPay && worldpayAvailable && (
                    <button
                      type="button"
                      onClick={() => {
                        closeViewModal();
                        setWorldpayResult(null);
                        setWorldpayModalPayment(payment);
                      }}
                      className="inline-flex items-center px-4 py-2 text-sm font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-md hover:bg-indigo-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1 transition-colors"
                    >
                      Take card payment by phone (Worldpay)
                    </button>
                  )}

                  {canVoid && (
                    <button
                      type="button"
                      onClick={() => {
                        closeViewModal();
                        openVoidModal(payment);
                      }}
                      className="inline-flex items-center px-4 py-2 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-md hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1 transition-colors"
                    >
                      Void Payment
                    </button>
                  )}
                </div>
              </div>
            </div>
          </Modal>
        );
      })()}

      {/* Finance visibility is never gated by WORLDPAY_ENABLED. A rollback
          blocks starting/confirming new Worldpay payments, but both existing
          provider incidents and payment-side reconciliation flags must remain
          reviewable. Each queue renders nothing after a successful empty
          fetch, so mounting both unconditionally adds no empty-state noise. */}
      <WorldpayUnmatchedQueue />
      <WorldpayNeedsReconciliationQueue />

      {error && (
        <div className="admin-payments-error">
          {error}
        </div>
      )}

        <div className="admin-payments-summary">
    <div className="summary-card">
      <h4>Total records</h4>
      <p>{globalStats?.total ?? '—'}</p>
    </div>
    <div className="summary-card">
      <h4>Pending</h4>
      <p>{globalStats?.pending ?? '—'}</p>
    </div>
    <div className="summary-card">
      <h4>Paid</h4>
      <p>{globalStats?.paid ?? '—'}</p>
    </div>
    <div className="summary-card">
      <h4>Voided</h4>
      <p>{globalStats?.voided ?? '—'}</p>
    </div>
    <div className="summary-card">
      <h4>Monies owed</h4>
      <p>{globalStats ? currencyFormatter.format(globalStats.owed) : '—'}</p>
    </div>
    <div className="summary-card">
      <h4>Monies paid</h4>
      <p>{globalStats ? currencyFormatter.format(globalStats.paidAmount) : '—'}</p>
    </div>
  </div>

      <div className="admin-payments-actions">
        <form onSubmit={handleSearchSubmit}>
          <input
            type="text"
            placeholder="Search by email, name, or payment ID"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <button type="submit" className="primary">Search</button>
          {appliedSearch && (
            <button type="button" className="secondary" onClick={handleClearSearch}>
              Clear
            </button>
          )}
        </form>

        <select
          value={statusFilter}
          onChange={(e) => handleStatusFilterChange(e.target.value as StatusFilter)}
        >
          {statusOptions.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>

        <select
          value={pageSize}
          onChange={(e) => handlePageSizeChange(Number(e.target.value))}
        >
          {[25, 50, 100, 200].map((value) => (
            <option key={value} value={value}>
              {value} per page
            </option>
          ))}
        </select>

        <button type="button" className="secondary" onClick={handleRefresh}>
          Refresh
        </button>

        <span className="text-sm text-gray-500 ml-auto">
          {totalCount > 0 ? `${totalCount.toLocaleString()} total records` : ''}
        </span>
      </div>

      {loading ? (
        <div className="admin-payments-loading">Loading payments...</div>
      ) : payments.length === 0 ? (
        <div className="admin-payments-empty">
          No payments found for the selected filters.
        </div>
      ) : (
        <div className="w-full overflow-x-auto rounded-lg border border-gray-200 bg-white">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Created</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Client</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Email</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Reason</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">Amount</th>
                <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wide text-gray-500 w-24">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {payments.map((payment) => {
                const status = (payment.status || '').toLowerCase();
                const reinstatedAt = payment.reinstatedAt;
                const isReinstated = Boolean(reinstatedAt);
                const isVoided = status === 'voided';
                const chaserStatus = getChaserStatus(payment);
                return (
                  <tr
                    key={payment.id}
                    className={`${getPaymentRowClass(payment)} transition-colors`}
                  >
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">
                      {formatDate(payment.createdAt)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-900">
                      {payment.clientName || '---'}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">
                      {payment.clientEmail}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <div className="flex flex-col items-start gap-1">
                        <span className={`status-pill ${status || 'pending'}`}>
                          {status || 'pending'}
                        </span>
                        {isReinstated && (
                          <span
                            className="status-pill reinstated"
                            title={formatDate(reinstatedAt)}
                          >
                            reinstated
                          </span>
                        )}
                        {isVoided && (
                          <span className="inline-flex items-center px-1.5 py-0.5 text-xs font-medium rounded bg-gray-200 text-gray-600">
                            voided
                          </span>
                        )}
                        {payment.isChecked && (
                          <span className="inline-flex items-center px-1.5 py-0.5 text-xs font-medium rounded bg-green-100 text-green-700">
                            checked
                          </span>
                        )}
                        {chaserStatus === 'chased' && (
                          <span
                            className="inline-flex items-center px-1.5 py-0.5 text-xs font-medium rounded bg-orange-100 text-orange-700"
                            title={`Chaser sent: ${formatDate(payment.chaserSentAt)}`}
                          >
                            chased
                          </span>
                        )}
                        {chaserStatus === 'overdue' && (
                          <span
                            className="inline-flex items-center px-1.5 py-0.5 text-xs font-medium rounded bg-red-100 text-red-700"
                            title={`Chaser sent: ${formatDate(payment.chaserSentAt)} - Overdue for escalation`}
                          >
                            overdue
                          </span>
                        )}
                        <WorldpayConfirmationBadge confirmation={payment.worldpayConfirmation} />
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 max-w-[200px]">
                      {payment.cancellationReason ? (
                        <span
                          className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded truncate ${
                            payment.cancellationReason.toLowerCase().includes('non-coop') ||
                            payment.cancellationReason.toLowerCase().includes('non coop')
                              ? 'bg-red-100 text-red-800'
                              : payment.cancellationReason.toLowerCase().includes('client request')
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-gray-100 text-gray-700'
                          }`}
                          title={payment.cancellationReason}
                        >
                          {payment.cancellationReason.length > 30
                            ? `${payment.cancellationReason.substring(0, 30)}...`
                            : payment.cancellationReason}
                        </span>
                      ) : (
                        <span className="text-gray-400 text-xs">—</span>
                      )}
                    </td>
                    <td className={`whitespace-nowrap px-4 py-3 text-sm font-medium text-right ${isVoided ? 'text-gray-400 line-through' : 'text-gray-900'}`}>
                      {currencyFormatter.format(payment.amount || 0)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-center">
                      <button
                        type="button"
                        onClick={() => openViewModal(payment)}
                        className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 transition-colors"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="pagination-controls">
          <div className="pagination-info">
            Showing {((page - 1) * pageSize) + 1} - {Math.min(page * pageSize, totalCount)} of {totalCount.toLocaleString()}
          </div>
          <div className="pagination-buttons">
            <button
              type="button"
              onClick={() => setPage(1)}
              disabled={page === 1 || loading}
              className="pagination-btn"
              title="First page"
            >
              ««
            </button>
            <button
              type="button"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1 || loading}
              className="pagination-btn"
              title="Previous page"
            >
              «
            </button>
            <span className="pagination-current">
              Page {page} of {totalPages}
            </span>
            <button
              type="button"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages || loading}
              className="pagination-btn"
              title="Next page"
            >
              »
            </button>
            <button
              type="button"
              onClick={() => setPage(totalPages)}
              disabled={page === totalPages || loading}
              className="pagination-btn"
              title="Last page"
            >
              »»
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCancellationPayments;
